import { useEffect, useState } from "react";
import { Loader2, Film, Clock, Tv, Star, Building2, ExternalLink, User } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip } from "recharts";
import { getStats, getStudioStats, getDirectorStats, STATUS_LABELS } from "../lib/api";

const CHART_MARGIN = { left: 10, right: 20, bottom: 40 };
const AXIS_TICK = { fill: "#e5e5e5", fontSize: 11, fontFamily: "JetBrains Mono" };
const AXIS_TICK_MUTED = { fill: "#a3a3a3", fontSize: 11, fontFamily: "JetBrains Mono" };
const TOOLTIP_STYLE = { background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.2)", borderRadius: 0, fontFamily: "JetBrains Mono", fontSize: 11, textTransform: "uppercase" };
const TOOLTIP_CURSOR = { fill: "rgba(255,59,48,0.08)" };
const BAR_COLORS = ["#FF3B30", "#E5342A", "#B32820", "#8A1F19", "#4D110E"];

export default function Stats() {
  const [stats, setStats] = useState(null);
  const [studioStats, setStudioStats] = useState(null);
  const [directorStats, setDirectorStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, ss, ds] = await Promise.all([getStats(), getStudioStats(), getDirectorStats()]);
        setStats(s);
        setStudioStats(ss);
        setDirectorStats(ds);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2 className="h-6 w-6 animate-spin text-[#FF3B30]" />
      </div>
    );
  }

  if (!stats || stats.empty) {
    return (
      <div className="mx-auto max-w-[1400px] px-6 py-16 md:px-10">
        <h1 className="font-display text-6xl uppercase text-white">No stats yet</h1>
        <p className="mt-3 text-neutral-500">Add anime to your catalogue to unlock stats.</p>
      </div>
    );
  }

  const genreData = Object.entries(stats.genre_breakdown || {})
    .slice(0, 12)
    .map(([name, count]) => ({ name, count }));

  return (
    <div className="mx-auto max-w-[1600px] px-6 py-10 md:px-10">
      <p className="font-mono text-[11px] uppercase tracking-[0.4em] text-[#FF3B30]">// Vol. 02</p>
      <h1 className="mt-2 font-display text-6xl uppercase leading-[0.9] tracking-tight text-white md:text-8xl">
        Receipts
      </h1>
      <p className="mt-4 max-w-lg text-sm text-neutral-400">
        Everything you&apos;ve watched, in cold numbers.
      </p>

      {/* Big stat bento */}
      <div className="mt-10 grid grid-cols-2 gap-px bg-white/10 md:grid-cols-4">
        <StatCell icon={<Tv />} label="Anime" value={stats.total_anime} />
        <StatCell icon={<Film />} label="Episodes" value={stats.total_episodes} />
        <StatCell icon={<Clock />} label="Watch Hours" value={stats.total_watch_time_hours} sub={`${stats.total_watch_time_days} days`} />
        <StatCell icon={<Star />} label="Avg Rating" value={stats.average_user_rating ?? "—"} accent />
      </div>

      {/* By status */}
      <section className="mt-14">
        <h2 className="font-display text-3xl uppercase tracking-widest text-white">By Status</h2>
        <div className="mt-4 grid grid-cols-2 gap-px bg-white/10 md:grid-cols-5">
          {Object.entries(STATUS_LABELS).map(([k, label]) => (
            <div key={k} className="bg-neutral-950 p-5">
              <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">{label}</p>
              <p className="mt-2 font-display text-4xl text-white">{stats.by_status?.[k] || 0}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Genre chart */}
      <section className="mt-14">
        <h2 className="font-display text-3xl uppercase tracking-widest text-white">Genre Breakdown</h2>
        <div className="mt-6 border border-white/10 bg-neutral-950 p-6">
          <ResponsiveContainer width="100%" height={420}>
            <BarChart data={genreData} margin={CHART_MARGIN}>
              <XAxis
                dataKey="name"
                stroke="#666"
                tick={AXIS_TICK}
                angle={-40}
                textAnchor="end"
                interval={0}
                height={90}
              />
              <YAxis stroke="#666" tick={AXIS_TICK_MUTED} allowDecimals={false} />
              <Tooltip contentStyle={TOOLTIP_STYLE} cursor={TOOLTIP_CURSOR} />
              <Bar dataKey="count" isAnimationActive={false}>
                {genreData.map((entry, i) => {
                  const fill = i < BAR_COLORS.length ? BAR_COLORS[i] : "#3a3a3a";
                  return <Cell key={entry.name} fill={fill} />;
                })}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>

      {/* Studios & Staff — new section */}
      <StudiosSection data={studioStats} />
      <DirectorsSubSection data={directorStats} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Studios & Staff — ranked list + completion bars vs. AniList catalogue size.
// Reuses the same chart palette and dark-card visual language as the Genre
// chart above so it slots into the existing Receipts flow.
// ---------------------------------------------------------------------------
function StudiosSection({ data }) {
  const [sortBy, setSortBy] = useState("count"); // "count" | "hours"

  if (!data || data.empty || !data.studios?.length) return null;

  const rows = [...data.studios].sort((a, b) => {
    if (sortBy === "hours") return (b.watched_hours || 0) - (a.watched_hours || 0);
    return (b.watched_count || 0) - (a.watched_count || 0);
  });

  const chartData = rows.slice(0, 8).map((s) => ({
    name: s.name,
    Count: s.watched_count,
    Hours: s.watched_hours,
  }));
  const chartKey = sortBy === "hours" ? "Hours" : "Count";

  return (
    <section className="mt-14" data-testid="studios-section">
      <div className="flex flex-wrap items-baseline justify-between gap-4">
        <div className="flex items-center gap-3">
          <Building2 className="h-5 w-5 text-[#FF3B30]" />
          <h2 className="font-display text-3xl uppercase tracking-widest text-white">Studios &amp; Staff</h2>
        </div>
        <div className="flex items-center gap-2 border border-white/15" role="tablist">
          <button
            data-testid="studios-sort-count"
            onClick={() => setSortBy("count")}
            className={`px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest ${sortBy === "count" ? "bg-[#FF3B30] text-white" : "text-neutral-400 hover:text-white"}`}
          >
            By Titles
          </button>
          <button
            data-testid="studios-sort-hours"
            onClick={() => setSortBy("hours")}
            className={`px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest ${sortBy === "hours" ? "bg-[#FF3B30] text-white" : "text-neutral-400 hover:text-white"}`}
          >
            By Hours
          </button>
        </div>
      </div>

      {/* Chart — same visual style as the Genre chart */}
      <div className="mt-6 border border-white/10 bg-neutral-950 p-6">
        <ResponsiveContainer width="100%" height={360}>
          <BarChart data={chartData} margin={CHART_MARGIN}>
            <XAxis
              dataKey="name"
              stroke="#666"
              tick={AXIS_TICK}
              angle={-35}
              textAnchor="end"
              interval={0}
              height={90}
            />
            <YAxis stroke="#666" tick={AXIS_TICK_MUTED} allowDecimals={false} />
            <Tooltip contentStyle={TOOLTIP_STYLE} cursor={TOOLTIP_CURSOR} />
            <Bar dataKey={chartKey} isAnimationActive={false}>
              {chartData.map((entry, i) => (
                <Cell key={entry.name} fill={i < BAR_COLORS.length ? BAR_COLORS[i] : "#3a3a3a"} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Ranked list with completion bars */}
      <div className="mt-6 grid grid-cols-1 gap-3 md:grid-cols-2" data-testid="studios-list">
        {rows.map((s, i) => (
          <StudioCard key={s.name} rank={i + 1} studio={s} />
        ))}
      </div>

      <p className="mt-4 font-mono text-[10px] uppercase tracking-widest text-neutral-500">
        Completion = titles you&apos;ve added ÷ studio&apos;s known main-role catalogue on AniList
      </p>
    </section>
  );
}

function StudioCard({ rank, studio }) {
  const pct = studio.completion_pct;
  const total = studio.total_count;
  const totalLabel = total == null ? "?" : `${total}${studio.is_capped ? "+" : ""}`;
  return (
    <div
      data-testid={`studio-row-${rank}`}
      className="border border-white/10 bg-neutral-950 p-4 transition-colors hover:border-[#FF3B30]/40"
    >
      <div className="flex items-baseline justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">#{rank}</span>
          {studio.url ? (
            <a
              href={studio.url}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-1 font-display text-2xl uppercase tracking-wide text-white hover:text-[#FF3B30]"
            >
              {studio.name}
              <ExternalLink className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-70" />
            </a>
          ) : (
            <p className="font-display text-2xl uppercase tracking-wide text-white">{studio.name}</p>
          )}
        </div>
        <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
          {studio.watched_hours ?? 0} hrs
        </p>
      </div>

      <div className="mt-3 flex items-baseline justify-between font-mono text-[11px] uppercase tracking-widest">
        <span className="text-white">
          {studio.watched_count} <span className="text-neutral-500">of {totalLabel}</span>
        </span>
        {pct != null && (
          <span className={pct >= 25 ? "text-[#FF3B30]" : "text-neutral-400"}>
            {pct}%
          </span>
        )}
      </div>
      <div className="mt-2 h-1.5 w-full bg-white/5">
        {pct != null ? (
          <div
            className="h-full bg-[#FF3B30] transition-all"
            style={{ width: `${Math.max(pct, 2)}%` }}
          />
        ) : (
          <div className="h-full w-full bg-white/10" />
        )}
      </div>
    </div>
  );
}

function StatCell({ icon, label, value, sub, accent }) {
  return (
    <div className={`bg-neutral-950 p-6 ${accent ? "border-l-2 border-[#FF3B30]" : ""}`}>
      <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-neutral-500">
        <span className="[&>svg]:h-3 [&>svg]:w-3">{icon}</span>
        {label}
      </div>
      <p className="mt-3 font-display text-5xl leading-none text-white md:text-6xl">{value}</p>
      {sub && <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-neutral-500">{sub}</p>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Directors sub-section — sits under Studios in "Studios & Staff". Only
// renders when at least one catalogue row has director data (rows added
// before iteration 38 don't have it — we don't backfill silently).
// ---------------------------------------------------------------------------
function DirectorsSubSection({ data }) {
  if (!data) return null;
  if (data.empty) {
    if (data.reason === "no_director_data") {
      return (
        <div className="mt-8 border border-dashed border-white/10 bg-neutral-950 px-6 py-5" data-testid="directors-empty">
          <div className="flex items-center gap-2">
            <User className="h-4 w-4 text-neutral-500" />
            <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
              Directors — no data yet
            </p>
          </div>
          <p className="mt-2 text-xs text-neutral-500">
            Director credits are pulled at add-time from AniList. Add or re-add a show and it&apos;ll show up here.
          </p>
        </div>
      );
    }
    return null;
  }
  const max = Math.max(...data.directors.map((d) => d.watched_count), 1);
  return (
    <div className="mt-10" data-testid="directors-section">
      <div className="flex items-baseline justify-between border-b border-white/10 pb-2">
        <div className="flex items-center gap-2">
          <User className="h-4 w-4 text-[#FF3B30]" />
          <h3 className="font-display text-2xl uppercase tracking-widest text-white">Directors</h3>
        </div>
        <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
          {data.coverage_count} of {data.total_titles} titles indexed
        </span>
      </div>
      <div className="mt-4 grid grid-cols-1 gap-2" data-testid="directors-list">
        {data.directors.map((d, i) => (
          <div key={d.name} className="border border-white/10 bg-neutral-950 px-4 py-3" data-testid={`director-row-${i + 1}`}>
            <div className="flex items-baseline justify-between font-mono text-[11px] uppercase tracking-widest">
              <span className="text-white">#{i + 1} · {d.name}</span>
              <span className="text-neutral-500">{d.watched_count} · {d.watched_hours} hrs</span>
            </div>
            <div className="mt-2 h-1 w-full bg-white/5">
              <div
                className="h-full bg-[#FF3B30]"
                style={{ width: `${(d.watched_count / max) * 100}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

